import { Controller } from "@hotwired/stimulus"

// Live search for the navbar box: fetches matching products and categories while the customer
// types and shows them in a dropdown, with arrow-key navigation. Pressing Enter without
// picking a suggestion submits the form and lands on the full results page, exactly as before.
export default class extends Controller {
  static targets = ["input", "panel"]
  static values = {
    url: String,
    minLength: { type: Number, default: 2 },
    delay: { type: Number, default: 250 }
  }

  connect() {
    this.index = -1
    // Clicks land on the document before a link's own click when the panel steals focus, so
    // the panel is closed from here rather than on blur.
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    clearTimeout(this.timer)
    this.controller?.abort()
  }

  // Typing: debounce, then fetch.
  search() {
    clearTimeout(this.timer)
    const query = this.inputTarget.value.trim()

    if (query.length < this.minLengthValue) {
      this.close()
      return
    }

    this.timer = setTimeout(() => this.fetchResults(query), this.delayValue)
  }

  // Re-open the last results when the box regains focus.
  focus() {
    if (this.panelTarget.innerHTML.trim() !== "") this.open()
  }

  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter": {
        const active = this.items[this.index]
        if (active) {
          event.preventDefault()
          active.click()
        }
        break
      }
      case "Escape":
        this.close()
        this.inputTarget.blur()
        break
    }
  }

  // -- internals --

  async fetchResults(query) {
    // Drop any in-flight request so a slow early response cannot overwrite a newer one.
    this.controller?.abort()
    this.controller = new AbortController()

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "text/html" },
        signal: this.controller.signal
      })
      if (!response.ok) return this.close()

      const html = await response.text()
      this.panelTarget.innerHTML = html
      this.index = -1
      html.trim() === "" ? this.close() : this.open()
    } catch (error) {
      if (error.name !== "AbortError") this.close()
    }
  }

  move(step) {
    const items = this.items
    if (items.length === 0) return

    this.index = (this.index + step + items.length) % items.length
    items.forEach((item, position) => {
      const current = position === this.index
      item.classList.toggle("bg-emerald-50", current)
      if (current) item.scrollIntoView({ block: "nearest" })
    })
  }

  open() {
    this.panelTarget.classList.remove("hidden")
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.index = -1
  }

  get items() {
    return Array.from(this.panelTarget.querySelectorAll("[data-search-item]"))
  }
}
